import {
  appBorders,
  appColors,
  CareerCategory,
  CareerPath,
  ContentNugget,
  SIDEBAR_COLLAPSED_WIDTH,
} from '@backrs_ui/common-ui';

import { appStrings } from '@backrs_ui/grow-ui/src/assets/appStrings';
import { CareerPathColumnGroup } from '@backrs_ui/grow-ui/src/components/CareerPath/CareerPathColumns';
import { setCurrentDialogNugget } from '@backrs_ui/grow-ui/src/redux/contentNugget/contentNugget';
import { useAppDispatch } from '@backrs_ui/grow-ui/src/redux/hooks';

import { Box, SxProps, Theme, Typography } from '@mui/material';
import { usePostHog } from 'posthog-js/react';

const DIALOG_STRINGS = appStrings.modules.start.careerPathDialog;

type CareerPathPageProps = Readonly<{
  careerCategories: CareerCategory[];
  careerPaths: CareerPath[];
}>;

export const CareerPathPage = ({ careerCategories, careerPaths }: CareerPathPageProps) => {
  const postHog = usePostHog();

  const dispatch = useAppDispatch();

  function handleSettingCareerPathDialog(path: CareerPath) {
    if (!path) {
      return;
    }

    postHog.capture('opened_career_path', {
      id: path.id,
      name: path.name,
      page: 'Career Paths',
    });

    const careerPathNugget: ContentNugget = {
      id: path.id,
      title: path.name,
      description: path.description,
      slug: path.id.toString(),
      star_rating: 0,
      value_rating: 0,
      variant: 'career-path',
      content_nugget_theme: 'Career Path',
      is_browsable: false,
      career_path_info: path,
    };

    dispatch(setCurrentDialogNugget(careerPathNugget));
  }

  return (
    <Box sx={containerStyle}>
      <Box sx={contentContainerStyle}>
        <Box sx={textAndDiagramContainerStyle}>
          <Box sx={textContainerStyle}>
            <Typography variant="h4" sx={headerTextStyle}>
              {DIALOG_STRINGS.title}
            </Typography>
            {DIALOG_STRINGS.content.map((text, index) => (
              <Typography key={index} variant="body1" sx={bodyTextStyle}>
                {text}
              </Typography>
            ))}
          </Box>

          <Box sx={diagramContainerStyle}>
            <Box sx={columnsContainerStyle}>
              {careerCategories.map((category, index) => (
                <CareerPathColumnGroup
                  key={index}
                  index={index}
                  careerPaths={careerPaths}
                  category={category}
                  setCurrentDialogCareerPath={handleSettingCareerPathDialog}
                />
              ))}
            </Box>
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

const containerStyle: SxProps<Theme> = {
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  flexGrow: 1,
  minHeight: '100svh',
  py: {
    xs: 3,
    sm: 2,
    md: 2,
  },
  px: {
    xs: 3,
    sm: 4,
  },
  boxSizing: 'border-box',
  maxHeight: '100svh',
  ml: `${SIDEBAR_COLLAPSED_WIDTH}px`,
} as const;

const contentContainerStyle: SxProps<Theme> = {
  backgroundColor: appColors.neutral.white,
  border: appBorders.backrsBorders.black,
  boxShadow: appBorders.backrsDropShadowBorders.black.boxShadow,
  borderRadius: 4,
  maxWidth: '100%',
  boxSizing: 'border-box',
  alignSelf: 'center',
  justifySelf: 'center',
  overflowY: 'auto',
} as const;

const bodyTextStyle: SxProps<Theme> = {
  mb: 2,
  fontSize: '12pt',
  '@media (max-height: 768px)': {
    fontSize: '11pt',
  },
} as const;

const textAndDiagramContainerStyle: SxProps<Theme> = {
  display: 'flex',
  flexDirection: {
    xs: 'column',
    sm: 'column',
    md: 'row',
    xl: 'row',
  },
  justifyContent: 'flex-start',
  alignItems: 'flex-start',
  paddingX: { xs: 2, sm: 4 },
  paddingTop: 4,
  boxSizing: 'border-box',
  height: '100%',
  '@media (max-height: 768px)': {
    paddingTop: 2,
    paddingBottom: 1,
  },
} as const;

const textContainerStyle: SxProps<Theme> = {
  maxWidth: 500,
  pr: 4,
  overflowY: 'auto',
} as const;

const headerTextStyle: SxProps<Theme> = {
  fontWeight: 700,
  mb: { xs: 2, sm: 4 },
  fontSize: { xs: '16pt', sm: '24pt' },
  '@media (max-height: 768px)': {
    fontSize: '20pt',
    mb: 2,
  },
} as const;

const diagramContainerStyle: SxProps<Theme> = {
  display: 'flex',
} as const;

const columnsContainerStyle: SxProps<Theme> = {
  flexDirection: 'column',
  display: 'flex',
  maxHeight: 700,
  overflow: 'auto',
  flexWrap: 'wrap',
  '@media (max-width: 1300px)': {
    maxHeight: '1100px',
  },
  '@media (max-width: 1100px)': {
    maxHeight: 'unset',
  },
  '@media (max-width: 900px)': {
    maxHeight: '1200px',
  },
  '@media (max-width: 768px)': {
    maxHeight: 'unset',
  },
} as const;
